import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import Pagination from "../components/Pagination";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  CurrencyCircleDollar,
  Plus,
  Spinner,
  Trash,
  Check,
  Lightning,
  PencilSimple,
  Power,
  Lock,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { getErrorMessage } from "../lib/utils";
import { useTranslation } from "react-i18next";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_SINGLE_TIER = { min_kwh: 0, max_kwh: null, price_per_kwh: 0 };

const DEFAULT_TIERS = [
  { min_kwh: 0, max_kwh: 250, price_per_kwh: 4.8044 },
  { min_kwh: 250, max_kwh: 500, price_per_kwh: 9.9115 },
  { min_kwh: 500, max_kwh: 750, price_per_kwh: 10.6573 },
  { min_kwh: 750, max_kwh: 1000, price_per_kwh: 11.5519 },
  { min_kwh: 1000, max_kwh: null, price_per_kwh: 13.8069 },
];

const TariffsPage = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [tariffs, setTariffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTariff, setEditingTariff] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [tariffPage, setTariffPage] = useState(1);
  const [tariffPageSize, setTariffPageSize] = useState(10);
  const [tariffType, setTariffType] = useState("tiered");
  const [formData, setFormData] = useState({
    name: "",
    tiers: [...DEFAULT_TIERS],
  });

  const canCreateCustom = user?.permissions?.tariff_set === true || user?.permissions?.custom_tariff === true;

  const fetchTariffs = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/tariffs`);
      setTariffs(response.data);
    } catch (error) {
      toast.error("Tarifeler yuklenirken hata olustu");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTariffs();
  }, [fetchTariffs]);

  const openCreateDialog = () => {
    setEditingTariff(null);
    setTariffType("tiered");
    setFormData({ name: "", tiers: [...DEFAULT_TIERS] });
    setDialogOpen(true);
  };

  const openEditDialog = (tariff) => {
    setEditingTariff(tariff);
    setTariffType(tariff.tiers.length === 1 ? "single" : "tiered");
    setFormData({ name: tariff.name, tiers: [...tariff.tiers] });
    setDialogOpen(true);
  };

  const handleTariffTypeChange = (type) => {
    setTariffType(type);
    if (type === "single") {
      setFormData({ ...formData, tiers: [{ ...EMPTY_SINGLE_TIER }] });
    } else {
      setFormData({ ...formData, tiers: [...DEFAULT_TIERS] });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      if (editingTariff) {
        await axios.put(`${API_URL}/api/tariffs/${editingTariff.id}`, formData);
        toast.success("Tarife guncellendi");
      } else {
        await axios.post(`${API_URL}/api/tariffs`, formData);
        toast.success("Tarife olusturuldu");
      }
      setDialogOpen(false);
      setEditingTariff(null);
      fetchTariffs();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteTariff = async (tariffId) => {
    if (!window.confirm("Bu tarifeyi silmek istediginize emin misiniz?")) return;
    try {
      await axios.delete(`${API_URL}/api/tariffs/${tariffId}`);
      toast.success("Tarife silindi");
      fetchTariffs();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleActivateTariff = async (tariffId) => {
    try {
      await axios.put(`${API_URL}/api/tariffs/${tariffId}/activate`);
      toast.success("Tarife aktif yapildi");
      fetchTariffs();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const addTier = () => {
    const lastTier = formData.tiers[formData.tiers.length - 1];
    const newMin = lastTier?.max_kwh || 0;
    setFormData({
      ...formData,
      tiers: [
        ...formData.tiers.slice(0, -1),
        { ...lastTier, max_kwh: newMin + 250 },
        { min_kwh: newMin + 250, max_kwh: null, price_per_kwh: lastTier?.price_per_kwh || 10 },
      ],
    });
  };

  const removeTier = (index) => {
    if (formData.tiers.length <= 1) return;
    const newTiers = formData.tiers.filter((_, i) => i !== index);
    if (newTiers.length > 0) {
      newTiers[newTiers.length - 1].max_kwh = null;
    }
    setFormData({ ...formData, tiers: newTiers });
  };

  const updateTier = (index, field, value) => {
    const newTiers = [...formData.tiers];
    newTiers[index][field] = value === "" ? null : parseFloat(value);
    setFormData({ ...formData, tiers: newTiers });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size={32} className="animate-spin text-indigo-600" />
      </div>
    );
  }

  const activeTariff = tariffs.find((t) => t.is_active);

  return (
    <div className="space-y-6" data-testid="tariffs-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">
            Tarife Ayarlari
          </h1>
          <p className="text-slate-500 mt-1">
            {canCreateCustom
              ? "Sistem tarifelerini goruntuleyebilir, kendi ozel tarifenizi olusturabilirsiniz"
              : "Sistem tarifelerini goruntuleyebilirsiniz"}
          </p>
        </div>
        {canCreateCustom && (
          <Button
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="create-tariff-btn"
            onClick={openCreateDialog}
          >
            <Plus size={18} className="mr-2" />
            Yeni Tarife
          </Button>
        )}
      </div>

      {/* No Permission Info */}
      {!canCreateCustom && (
        <Card className="border-slate-200 bg-slate-50/50 shadow-sm" data-testid="no-custom-tariff-info">
          <CardContent className="p-4 flex items-center gap-3">
            <Lock size={20} className="text-slate-400" />
            <p className="text-sm text-slate-600">
              Ozel tarife olusturma yetkiniz bulunmuyor. Sadece sistem tarifelerini goruntuleyebilirsiniz. Yetki icin admin ile iletisime gecin.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Active Tariff (only show own active tariff) */}
      {activeTariff && !activeTariff.is_system && (
        <Card className="border-indigo-200 bg-indigo-50/50 shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <CurrencyCircleDollar size={24} className="text-indigo-600" />
                Aktif Tarife: {activeTariff.name}
              </CardTitle>
              <div className="flex items-center gap-2">
                <Badge className="bg-indigo-100 text-indigo-700">
                  <Check size={14} className="mr-1" />
                  Aktif
                </Badge>
                {canCreateCustom && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => openEditDialog(activeTariff)}
                    data-testid="edit-active-tariff-btn"
                    className="text-indigo-600 hover:text-indigo-700"
                  >
                    <PencilSimple size={16} />
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className={`grid grid-cols-1 gap-4 ${activeTariff.tiers.length === 1 ? 'md:grid-cols-1 max-w-xs' : 'md:grid-cols-2 lg:grid-cols-5'}`}>
              {activeTariff.tiers?.map((tier, index) => (
                <div
                  key={index}
                  className="p-4 bg-white rounded-xl border border-indigo-100"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Lightning size={18} className="text-indigo-500" />
                    <span className="text-sm font-medium text-slate-600">
                      {activeTariff.tiers.length === 1 ? "Sabit Fiyat" : `Kademe ${index + 1}`}
                    </span>
                  </div>
                  {activeTariff.tiers.length > 1 && (
                    <p className="text-sm text-slate-500 mb-1">
                      {tier.min_kwh} - {tier.max_kwh || "\u221e"} kWh
                    </p>
                  )}
                  <p className="text-xl font-bold text-indigo-600">
                    {"\u20ba"}{tier.price_per_kwh.toFixed(4)}/kWh
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tariff List */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg font-heading">Tum Tarifeler</CardTitle>
        </CardHeader>
        <CardContent>
          {tariffs.length === 0 ? (
            <div className="py-12 text-center">
              <CurrencyCircleDollar size={64} className="mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                Henuz tarife olusturulmamis
              </h3>
              <p className="text-slate-500">
                Yeni tarife olusturarak baslayin
              </p>
            </div>
          ) : (
          <>
            <div className="space-y-4">
              {tariffs.slice((tariffPage - 1) * tariffPageSize, tariffPage * tariffPageSize).map((tariff) => {
                const isSystem = tariff.is_system === true;
                const isOwn = !isSystem;
                return (
                <div
                  key={tariff.id}
                  className={`p-4 rounded-xl border ${
                    tariff.is_active && isOwn
                      ? "border-indigo-200 bg-indigo-50/30"
                      : isSystem
                      ? "border-emerald-200 bg-emerald-50/20"
                      : "border-slate-200 bg-slate-50"
                  }`}
                  data-testid={`tariff-item-${tariff.id}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <h4 className="font-medium text-slate-900">{tariff.name}</h4>
                      {isSystem && (
                        <Badge className="bg-emerald-100 text-emerald-700" data-testid={`system-badge-${tariff.id}`}>{t("staff.system")}</Badge>
                      )}
                      {isOwn && tariff.is_active && (
                        <Badge className="bg-indigo-100 text-indigo-700">{t("common.active")}</Badge>
                      )}
                      {isOwn && !tariff.is_active && (
                        <Badge variant="outline" className="text-slate-500">Ozel</Badge>
                      )}
                      <Badge variant="outline" className="text-xs">
                        {tariff.tiers.length === 1 ? "Tek Fiyat" : `${tariff.tiers.length} Kademe`}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      {tariff.created_at && (
                        <span className="text-sm text-slate-500">
                          {new Date(tariff.created_at).toLocaleDateString("tr-TR")}
                        </span>
                      )}
                      {isOwn && canCreateCustom && (
                        <>
                          {!tariff.is_active && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleActivateTariff(tariff.id)}
                              className="text-emerald-600 hover:text-emerald-700"
                              data-testid={`activate-tariff-${tariff.id}`}
                              title="Aktif yap"
                            >
                              <Power size={16} />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEditDialog(tariff)}
                            className="text-slate-600 hover:text-slate-700"
                            data-testid={`edit-tariff-${tariff.id}`}
                            title={t("common.edit")}
                          >
                            <PencilSimple size={16} />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteTariff(tariff.id)}
                            className="text-red-500 hover:text-red-600"
                            data-testid={`delete-tariff-${tariff.id}`}
                            title={t("common.delete")}
                          >
                            <Trash size={16} />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {tariff.tiers?.map((tier, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-white border border-slate-200 rounded-lg text-sm"
                      >
                        {tariff.tiers.length > 1 && <>{tier.min_kwh}-{tier.max_kwh || "\u221e"} kWh: </>}
                        <span className="font-semibold">{"\u20ba"}{tier.price_per_kwh.toFixed(4)}/kWh</span>
                      </span>
                    ))}
                  </div>
                </div>
                );
              })}
            </div>
            <Pagination total={tariffs.length} page={tariffPage} pageSize={tariffPageSize} onPageChange={setTariffPage} onPageSizeChange={setTariffPageSize} />
          </>
          )}
        </CardContent>
      </Card>
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) setEditingTariff(null); }}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTariff ? "Tarife Duzenle" : "Yeni Tarife Olustur"}</DialogTitle>
            <DialogDescription>
              {editingTariff ? "Tarife bilgilerini guncelleyin" : "Tek fiyat veya kademeli tarife olusturun"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">{t("tariffs.tariff_name")}</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ornegin: Standart Tarife"
                  required
                  data-testid="tariff-name-input"
                />
              </div>

              {/* Tariff Type Selector */}
              <div className="space-y-2">
                <Label>{t("tariffs.tariff_type")}</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => handleTariffTypeChange("single")}
                    className={`p-3 rounded-xl border text-sm font-medium transition-all ${
                      tariffType === "single"
                        ? "border-indigo-300 bg-indigo-50 text-indigo-700"
                        : "border-slate-200 hover:border-slate-300 text-slate-600"
                    }`}
                    data-testid="tariff-type-single"
                  >
                    Tek Fiyat
                  </button>
                  <button
                    type="button"
                    onClick={() => handleTariffTypeChange("tiered")}
                    className={`p-3 rounded-xl border text-sm font-medium transition-all ${
                      tariffType === "tiered"
                        ? "border-indigo-300 bg-indigo-50 text-indigo-700"
                        : "border-slate-200 hover:border-slate-300 text-slate-600"
                    }`}
                    data-testid="tariff-type-tiered"
                  >
                    Kademeli
                  </button>
                </div>
              </div>

              {tariffType === "single" ? (
                <div className="p-4 bg-slate-50 rounded-xl space-y-3">
                  <span className="text-sm font-medium text-slate-600">Sabit kWh Fiyati</span>
                  <div>
                    <Label className="text-xs">{"\u20ba"}/kWh</Label>
                    <Input
                      type="number"
                      step="0.0001"
                      value={formData.tiers[0]?.price_per_kwh || ""}
                      onChange={(e) => {
                        const val = e.target.value === "" ? 0 : parseFloat(e.target.value);
                        setFormData({
                          ...formData,
                          tiers: [{ min_kwh: 0, max_kwh: null, price_per_kwh: val }],
                        });
                      }}
                      data-testid="single-price-input"
                      required
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>{t("tariffs.tiers")}</Label>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={addTier}
                      data-testid="add-tier-btn"
                    >
                      <Plus size={14} className="mr-1" />
                      Kademe Ekle
                    </Button>
                  </div>

                  {formData.tiers.map((tier, index) => (
                    <div
                      key={index}
                      className="p-4 bg-slate-50 rounded-xl space-y-3"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-slate-600">
                          Kademe {index + 1}
                        </span>
                        {formData.tiers.length > 1 && (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => removeTier(index)}
                            className="text-red-500 hover:text-red-600 h-8 w-8 p-0"
                          >
                            <Trash size={16} />
                          </Button>
                        )}
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <Label className="text-xs">Min kWh</Label>
                          <Input
                            type="number"
                            step="1"
                            value={tier.min_kwh}
                            onChange={(e) => updateTier(index, "min_kwh", e.target.value)}
                            className="h-9"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Max kWh</Label>
                          <Input
                            type="number"
                            step="1"
                            placeholder={"\u221e"}
                            value={tier.max_kwh || ""}
                            onChange={(e) => updateTier(index, "max_kwh", e.target.value)}
                            className="h-9"
                            disabled={index === formData.tiers.length - 1}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">{"\u20ba"}/kWh</Label>
                          <Input
                            type="number"
                            step="0.0001"
                            value={tier.price_per_kwh}
                            onChange={(e) => updateTier(index, "price_per_kwh", e.target.value)}
                            className="h-9"
                            data-testid={`tier-price-input-${index}`}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => { setDialogOpen(false); setEditingTariff(null); }}
              >
                Iptal
              </Button>
              <Button
                type="submit"
                className="bg-indigo-600 hover:bg-indigo-700"
                disabled={formLoading}
                data-testid="tariff-submit-btn"
              >
                {formLoading ? "Kaydediliyor..." : editingTariff ? t("common.update") : t("common.create")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Info Card */}
      <Card className="border-amber-200 bg-amber-50/50 shadow-sm">
        <CardContent className="p-4">
          <p className="text-sm text-amber-800">
            <strong>Bilgi:</strong> {canCreateCustom
              ? "Yeni tarife olusturdugunuzda, mevcut aktif tarife otomatik olarak pasif hale gelir. Dairelere ozel tarife atamak icin Daireler sayfasini kullanabilirsiniz."
              : "Sistem tarifeleri admin tarafindan yonetilir. Ozel tarife olusturma yetkisi icin admin ile iletisime gecin."}
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default TariffsPage;
